import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ClerkProvider, useAuth, useUser } from "@clerk/clerk-react";
import { ConvexProviderWithClerk } from "convex/react-clerk";
import { ConvexReactClient } from "convex/react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Gleap from "gleap";
import { DeviceProvider } from "./contexts/DeviceContext";
import { LayoutProvider } from "./contexts/LayoutContext";
import { AvailableAppsProvider } from "./contexts/AvailableAppsContext";
import { config } from "./config/environment";
import ConvexErrorBoundary from "./components/ConvexErrorBoundary";
import { AuthGuard } from "./components/AuthGuard";
import { DeviceSerialGate } from "./components/DeviceSerialGate";
import Auth from "./pages/Auth";
import Index from "./pages/Index";
import AppDetail from "./pages/AppDetail";
import Updates from "./pages/Updates";
import Requests from "./pages/Requests";
import RequestedList from "./pages/requests/RequestedList";
import Support from "./pages/Support";

import Settings from "./pages/Settings";
import UserLevel from "./pages/settings/UserLevel";
import Apps from "./pages/settings/Apps";
import Browser from "./pages/settings/Browser";
import GeneralRestrictions from "./pages/settings/GeneralRestrictions";
import AppManagement from "./pages/settings/AppManagement";
import AppModeSelect from "./pages/settings/AppModeSelect";
import BrowserModeSelect from "./pages/settings/BrowserModeSelect";
import CategoryList from "./pages/settings/CategoryList";
import AppList from "./pages/settings/AppList";
import NotFound from "./pages/NotFound";
import NoDeviceError from "./pages/NoDeviceError";

import BottomNavigation from "./components/BottomNavigation";
import OfflineBanner from "./components/OfflineBanner";

const queryClient = new QueryClient();

// Convex client with better error handling
const convex = new ConvexReactClient(config.convex.url, { verbose: true });

// Scroll restoration component
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

const DeviceLayout = () => (
  <LayoutProvider>
    <DeviceProvider>
      <DeviceSerialGate>
        <AvailableAppsProvider>
          <ConvexErrorBoundary>
            <div className="relative animate-fade-in">
              <OfflineBanner />
              <ScrollToTop />
              <Routes>
                <Route path="/:deviceId" element={<Index />} />
                <Route path="/:deviceId/app/:appId" element={<AppDetail />} />
                <Route path="/:deviceId/updates" element={<Updates />} />
                {/* General route removed - profile now in header avatar */}
                <Route path="/:deviceId/requests" element={<Requests />} />
                <Route path="/:deviceId/requests/list" element={<RequestedList />} />
                <Route path="/:deviceId/support" element={<Support />} />
                
                <Route path="/:deviceId/settings" element={<Settings />} />
                <Route path="/:deviceId/settings/level" element={<UserLevel />} />
                <Route path="/:deviceId/settings/apps" element={<Apps />} />
                <Route path="/:deviceId/settings/browser" element={<Browser />} />
                <Route path="/:deviceId/settings/browser/mode" element={<BrowserModeSelect />} />
                <Route path="/:deviceId/settings/general" element={<GeneralRestrictions />} />
                <Route path="/:deviceId/settings/app-management" element={<AppManagement />} />
                <Route path="/:deviceId/settings/app-management/mode" element={<AppModeSelect />} />
                <Route path="/:deviceId/settings/app-management/categories/:type" element={<CategoryList />} />
                <Route path="/:deviceId/settings/app-management/:type" element={<AppList />} />
                <Route path="/" element={<Index />} />
                
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              <BottomNavigation />
            </div>
          </ConvexErrorBoundary>
        </AvailableAppsProvider>
      </DeviceSerialGate>
    </DeviceProvider>
  </LayoutProvider>
);

// Component to identify user with Gleap after sign-in
const GleapUserIdentifier = () => {
  const { user, isSignedIn } = useUser();

  useEffect(() => {
    if (isSignedIn && user) {
      const email = user.primaryEmailAddress?.emailAddress;
      const name = user.fullName || user.firstName || email?.split('@')[0] || 'User';
      
      if (email) {
        Gleap.identify(user.id, {
          email: email,
          name: name,
        });
      }
    } else {
      // Clear identity when signed out
      Gleap.clearIdentity();
    }
  }, [isSignedIn, user]);

  return null;
};

const App = () => {
  useEffect(() => {
    Gleap.initialize('w9gN3dxdGO56Kh2vzUjT4KwJ4nGiiX70');
    Gleap.showFeedbackButton(false);
    
    Gleap.hideAiChatbar();
  }, []);

  return (
    <ClerkProvider 
      publishableKey={config.clerk.publishableKey}
      afterSignOutUrl="/auth"
    >
      <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
        <GleapUserIdentifier />
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <Routes>
                <Route path="/auth" element={<Auth />} />
                <Route path="/*" element={
                  <AuthGuard>
                    <DeviceLayout />
                  </AuthGuard>
                } />
              </Routes>
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </ConvexProviderWithClerk>
    </ClerkProvider>
  );
};

export default App;
